// src/components/UserManagement.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore, functions } from '../firebase';
import { PERMISSION_KEYS, PERMISSION_LABELS } from '../constants/adminPermissions';
import { useAdminPermissions } from '../contexts/AdminPermissionsContext';
import './UserManagement.css';

const UserManagement = () => {
  const [user] = useAuthState(auth);
  const {
    isSuperAdmin,
    canClaimSuper,
    loading: permissionsLoading,
    superAdminReadError,
    superAdminDocExists,
  } = useAdminPermissions();
  const [legacyJudges, setLegacyJudges] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingSuper, setClaimingSuper] = useState(false);
  const [migratingJudges, setMigratingJudges] = useState(false);

  // Scores-only admin (email/password) — same as former “judge”
  const [scoreOnlyEmail, setScoreOnlyEmail] = useState('');
  const [scoreOnlyPassword, setScoreOnlyPassword] = useState('');
  const [scoreOnlyName, setScoreOnlyName] = useState('');
  const [creatingScoreOnly, setCreatingScoreOnly] = useState(false);

  // Admin form (email/password)
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Admin by UID (for existing Google users)
  const [adminUid, setAdminUid] = useState('');
  const [adminUidPermissions, setAdminUidPermissions] = useState([]);
  const [adminUidFullAccess, setAdminUidFullAccess] = useState(true);
  const [addingAdminByUid, setAddingAdminByUid] = useState(false);

  // Create admin permissions (email/password form)
  const [adminFullAccess, setAdminFullAccess] = useState(true);
  const [adminPermissions, setAdminPermissions] = useState([]);

  // Edit admin permissions
  const [editingAdminUid, setEditingAdminUid] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [editingFullAccess, setEditingFullAccess] = useState(true);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const loadData = async () => {
    const [judgesSnap, adminsSnap] = await Promise.all([
      getDocs(collection(firestore, 'judges')),
      getDocs(collection(firestore, 'admins')),
    ]);
    setLegacyJudges(judgesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setAdmins(adminsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        await loadData();
      } catch (err) {
        setError(err.message || 'Failed to load users.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  const handleClaimSuperAdmin = async () => {
    if (!user || !canClaimSuper) return;
    setError('');
    setClaimingSuper(true);
    try {
      await setDoc(doc(firestore, '_config', 'superAdmin'), { uid: user.uid });
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to set super admin.');
    } finally {
      setClaimingSuper(false);
    }
  };

  const handleMigrateJudges = async () => {
    if (!isSuperAdmin || legacyJudges.length === 0) return;
    if (!window.confirm(`Migrate ${legacyJudges.length} legacy judge record(s) to admins with Scores-only access?`)) return;
    setError('');
    setMigratingJudges(true);
    try {
      const migrateJudgesToAdmins = httpsCallable(functions, 'migrateJudgesToAdmins');
      await migrateJudgesToAdmins({});
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to migrate judges.');
    } finally {
      setMigratingJudges(false);
    }
  };

  const handleCreateScoreOnlyAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setCreatingScoreOnly(true);
    try {
      const createAdmin = httpsCallable(functions, 'createAdmin');
      await createAdmin({
        email: scoreOnlyEmail.trim(),
        password: scoreOnlyPassword,
        displayName: scoreOnlyName.trim() || undefined,
        permissions: ['scores'],
      });
      setScoreOnlyEmail('');
      setScoreOnlyPassword('');
      setScoreOnlyName('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create scores-only admin.');
    } finally {
      setCreatingScoreOnly(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    if (!adminFullAccess && adminPermissions.length === 0) {
      setError('Select at least one permission, or choose full access.');
      return;
    }
    setCreatingAdmin(true);
    try {
      const createAdmin = httpsCallable(functions, 'createAdmin');
      const permissions = adminFullAccess ? undefined : adminPermissions;
      await createAdmin({
        email: adminEmail.trim(),
        password: adminPassword,
        displayName: adminName.trim() || undefined,
        permissions,
      });
      setAdminEmail('');
      setAdminPassword('');
      setAdminName('');
      setAdminFullAccess(true);
      setAdminPermissions([]);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create admin.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleAddAdminByUid = async (e) => {
    e.preventDefault();
    const uid = adminUid.trim();
    if (!uid) return;
    if (!adminUidFullAccess && adminUidPermissions.length === 0) {
      setError('Select at least one permission, or choose full access.');
      return;
    }
    setError('');
    setAddingAdminByUid(true);
    try {
      const addAdminByUid = httpsCallable(functions, 'addAdminByUid');
      await addAdminByUid({
        uid,
        fullAccess: adminUidFullAccess,
        permissions: adminUidFullAccess ? undefined : adminUidPermissions,
      });
      setAdminUid('');
      setAdminUidFullAccess(true);
      setAdminUidPermissions([]);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to add admin.');
    } finally {
      setAddingAdminByUid(false);
    }
  };

  const handleDeleteAdmin = async (uid) => {
    if (!window.confirm('Remove admin access for this user in Fresh Meet?')) return;
    const deleteIdentityUser = window.confirm(
      'Also permanently delete their Fury Identity account? Cancel to keep Identity so they can still use other Fury apps.',
    );
    setError('');
    try {
      const deleteAdmin = httpsCallable(functions, 'deleteAdmin');
      await deleteAdmin({ uid, deleteIdentityUser });
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove admin.');
    }
  };

  const handleSaveEditPermissions = async () => {
    if (!editingAdminUid) return;
    if (!editingFullAccess && editingPermissions.length === 0) {
      setError('Select at least one permission, or choose full access.');
      return;
    }
    setError('');
    setSavingPermissions(true);
    try {
      const updateAdmin = httpsCallable(functions, 'updateAdmin');
      const permissions = editingFullAccess ? ['full'] : editingPermissions;
      await updateAdmin({ uid: editingAdminUid, permissions });
      setEditingAdminUid(null);
      setEditingPermissions([]);
      setEditingFullAccess(true);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update permissions.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const togglePermission = (arr, setArr, key) => {
    if (arr.includes(key)) setArr(arr.filter((p) => p !== key));
    else setArr([...arr, key]);
  };

  const formatPermissionsDisplay = (perms) => {
    if (!perms || perms.length === 0 || perms.includes('full')) return 'Full access';
    return perms.map((p) => PERMISSION_LABELS[p] || p).join(', ');
  };

  const formatDate = (v) => {
    if (!v || !v.toDate) return '—';
    return v.toDate().toLocaleDateString(undefined, { dateStyle: 'short' });
  };

  if (loading || permissionsLoading) {
    return <div className="user-management"><p>Loading...</p></div>;
  }

  return (
    <div className="user-management">
      <h2>Admins</h2>
      <p className="user-management-intro">
        Everyone signs in at <strong>/admin</strong> with a <strong>Fury Identity</strong> account (Google or email/password).
        Access is controlled by permissions on each admin document, or by the Identity <code>admin</code> claim for full access.
        Only the <strong>super admin</strong> can add, remove, or edit admins.
      </p>
      {error && <p className="user-management-error">{error}</p>}
      {superAdminReadError && (
        <p className="user-management-error">Could not read super-admin configuration: {superAdminReadError}</p>
      )}

      {canClaimSuper && (
        <section className="user-management-section user-management-claim">
          <p>
            {superAdminDocExists
              ? 'The super-admin document exists but has no usable uid (empty or missing). You can repair it — only one active super admin.'
              : 'No super admin document yet. As an admin, you can create it (only one super admin).'}
          </p>
          <button type="button" onClick={handleClaimSuperAdmin} disabled={claimingSuper}>
            {claimingSuper ? 'Setting…' : 'Set me as super admin'}
          </button>
        </section>
      )}

      {!isSuperAdmin && !canClaimSuper && (
        <p className="user-management-readonly">
          Only the super admin can add or remove admins. You can view the list below.
        </p>
      )}

      {legacyJudges.length > 0 && (
        <section className="user-management-section">
          <h3>Legacy judges</h3>
          <p className="user-management-hint">
            These Firestore documents predate the unified admin model. Migrate them so each person gets an <code>admins</code> entry with Scores-only access and signs in at /admin.
          </p>
          <table className="user-management-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>UID</th>
              </tr>
            </thead>
            <tbody>
              {legacyJudges.map((j) => (
                <tr key={j.id}>
                  <td>{j.email || '—'}</td>
                  <td>{j.displayName || '—'}</td>
                  <td className="user-management-uid">{j.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isSuperAdmin && (
            <button
              type="button"
              className="btn-fm-primary"
              disabled={migratingJudges}
              onClick={handleMigrateJudges}
            >
              {migratingJudges ? 'Migrating…' : 'Migrate legacy judges to admins'}
            </button>
          )}
        </section>
      )}

      <section className="user-management-section">
        <h3>Admin accounts</h3>
        <p className="user-management-hint">
          Grant access by email (creates a new Identity user) or by UID after the person has a Fury Identity account.
          “Add by UID” verifies the UID exists in Identity before writing their document in the <code>admins</code> collection.
        </p>
        <table className="user-management-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>UID</th>
              <th>Permissions</th>
              <th>Added</th>
              {isSuperAdmin && <th></th>}
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <React.Fragment key={a.id}>
                <tr>
                  <td>{a.email || '—'}</td>
                  <td>{a.displayName || '—'}</td>
                  <td className="user-management-uid">{a.id}</td>
                  <td className="user-management-permissions">
                    {formatPermissionsDisplay(a.permissions)}
                  </td>
                  <td>{formatDate(a.addedAt)}</td>
                  {isSuperAdmin && (
                    <td>
                      <button
                        type="button"
                        className="user-management-edit"
                        onClick={() => {
                          setEditingAdminUid(a.id);
                          setEditingFullAccess(!a.permissions || a.permissions.length === 0 || a.permissions.includes('full'));
                          setEditingPermissions(a.permissions && !a.permissions.includes('full') ? [...a.permissions] : []);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="user-management-remove"
                        onClick={() => handleDeleteAdmin(a.id)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
                {isSuperAdmin && editingAdminUid === a.id && (
                  <tr>
                    <td colSpan={isSuperAdmin ? 6 : 5} className="user-management-edit-row">
                      <div className="user-management-permission-editor">
                        <label>
                          <input
                            type="checkbox"
                            checked={editingFullAccess}
                            onChange={(e) => {
                              setEditingFullAccess(e.target.checked);
                              if (e.target.checked) setEditingPermissions([]);
                            }}
                          />
                          Full access
                        </label>
                        {!editingFullAccess && (
                          <div className="user-management-permission-list">
                            {PERMISSION_KEYS.map((key) => (
                              <label key={key}>
                                <input
                                  type="checkbox"
                                  checked={editingPermissions.includes(key)}
                                  onChange={() => togglePermission(editingPermissions, setEditingPermissions, key)}
                                />
                                {PERMISSION_LABELS[key]}
                              </label>
                            ))}
                          </div>
                        )}
                        <div>
                          <button type="button" onClick={handleSaveEditPermissions} disabled={savingPermissions}>
                            {savingPermissions ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={() => { setEditingAdminUid(null); setEditingPermissions([]); setEditingFullAccess(true); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {admins.length === 0 && <p className="user-management-empty">No admins in list (you may be in via Google).</p>}
        {isSuperAdmin && (
          <>
            <form className="user-management-form" onSubmit={handleCreateScoreOnlyAdmin}>
              <h4>Add scores-only admin (email)</h4>
              <p className="user-management-hint">Sky scores and related score routes only — same scope as the old judge role.</p>
              <label>
                Email <input type="email" value={scoreOnlyEmail} onChange={(e) => setScoreOnlyEmail(e.target.value)} required />
              </label>
              <label>
                Password (min 6) <input type="password" value={scoreOnlyPassword} onChange={(e) => setScoreOnlyPassword(e.target.value)} required minLength={6} />
              </label>
              <label>
                Display name <input type="text" value={scoreOnlyName} onChange={(e) => setScoreOnlyName(e.target.value)} placeholder="Optional" />
              </label>
              <button type="submit" className="btn-fm-primary" disabled={creatingScoreOnly}>{creatingScoreOnly ? 'Adding…' : 'Add scores-only admin'}</button>
            </form>
            <form className="user-management-form" onSubmit={handleCreateAdmin}>
              <h4>Add admin (by email)</h4>
              <label>
                Email <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
              </label>
              <label>
                Password (min 6) <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} required minLength={6} />
              </label>
              <label>
                Display name <input type="text" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Optional" />
              </label>
              <div className="user-management-permission-group">
                <label>
                  <input
                    type="checkbox"
                    checked={adminFullAccess}
                    onChange={(e) => {
                      setAdminFullAccess(e.target.checked);
                      if (e.target.checked) setAdminPermissions([]);
                    }}
                  />
                  Full access
                </label>
                {!adminFullAccess && (
                  <div className="user-management-permission-list">
                    {PERMISSION_KEYS.map((key) => (
                      <label key={key}>
                        <input
                          type="checkbox"
                          checked={adminPermissions.includes(key)}
                          onChange={() => togglePermission(adminPermissions, setAdminPermissions, key)}
                        />
                        {PERMISSION_LABELS[key]}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" className="btn-fm-primary" disabled={creatingAdmin}>{creatingAdmin ? 'Adding…' : 'Add admin'}</button>
            </form>
            <form className="user-management-form" onSubmit={handleAddAdminByUid}>
              <h4>Add admin by UID (Identity account must already exist)</h4>
              <label>
                UID <input type="text" value={adminUid} onChange={(e) => setAdminUid(e.target.value)} placeholder="Fury Identity UID" required />
              </label>
              <div className="user-management-permission-group">
                <label>
                  <input
                    type="checkbox"
                    checked={adminUidFullAccess}
                    onChange={(e) => {
                      setAdminUidFullAccess(e.target.checked);
                      if (e.target.checked) setAdminUidPermissions([]);
                    }}
                  />
                  Full access
                </label>
                {!adminUidFullAccess && (
                  <div className="user-management-permission-list">
                    {PERMISSION_KEYS.map((key) => (
                      <label key={key}>
                        <input
                          type="checkbox"
                          checked={adminUidPermissions.includes(key)}
                          onChange={() => togglePermission(adminUidPermissions, setAdminUidPermissions, key)}
                        />
                        {PERMISSION_LABELS[key]}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" className="btn-fm-primary" disabled={addingAdminByUid}>{addingAdminByUid ? 'Adding…' : 'Add by UID'}</button>
            </form>
          </>
        )}
      </section>
    </div>
  );
};

export default UserManagement;
